import { type FC, useState, useEffect } from 'react';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import {
    RecentlyViewedItemsProvider,
    type DeviceType,
    MOBILE,
} from 'dibs-recent-history/exports/RecentlyViewedItemsProvider.ts';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds.ts';
import { useHomePageContext } from '../HomePageContext.tsx';
import { HpSharedRecentlyViewedItems } from './HpSharedRecentlyViewedItems.tsx';
import { HpNewArrivalsCarousel } from './HpNewArrivalsCarousel.tsx';
import { HpRecentlyViewedShimmer } from './HpRecentlyViewedShimmer.tsx';

const MIN_RECENTLY_VIEWED_ITEMS = 3;

type Props = {
    moduleIndex: number;
    totalModules: number;
    deviceType: DeviceType;
};

export const HpSharedRecentlyViewedItemsModule: FC<Props> = ({
    moduleIndex,
    totalModules,
    deviceType,
}) => {
    const { isBot } = useHomePageContext();
    const [showNewArrivals, setShowNewArrivals] = useState<boolean | null>(null);
    const isMobile = deviceType === MOBILE;

    useEffect(() => {
        const recentlyViewedIds = getLocalHistoryItemIds({ isClient: true });
        setShowNewArrivals(recentlyViewedIds.length < MIN_RECENTLY_VIEWED_ITEMS);
    }, []);

    if (isBot) {
        return null;
    }

    const shimmer = <HpRecentlyViewedShimmer isMobile={isMobile} />;

    if (showNewArrivals === null) {
        return shimmer;
    }

    if (showNewArrivals) {
        return (
            <ClientSuspense fallback={shimmer}>
                <HpNewArrivalsCarousel />
            </ClientSuspense>
        );
    }

    return (
        <ClientSuspense fallback={shimmer}>
            <RecentlyViewedItemsProvider deviceType={deviceType}>
                <HpSharedRecentlyViewedItems
                    moduleIndex={moduleIndex}
                    totalModules={totalModules}
                    loadingComponent={shimmer}
                    hasBottomSeparator={false}
                />
            </RecentlyViewedItemsProvider>
        </ClientSuspense>
    );
};
